import { Card } from '@deckee/deck-hand';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { selectCurrentMember } from '../../../../../services/store/modules/backend-service';
import CaptionedIconLockup from '../../../../lockups/CaptionedIconLockup/CaptionedIconLockup';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import {
  ReactSvg,
  signalAppearanceAnonymous,
  signalAppearanceInvisible,
} from '../../../../shared/vector-images';
import MemberAvatar from '../../../../shared/_deprecated/components/MemberAvatar/MemberAvatar';
import { SignalAppearanceTypes } from './SignalAppearance.types';

export interface SignalAppearanceButtonProps {
  onClick: () => void;
  active?: boolean;
  loading?: boolean;
  type: SignalAppearanceTypes;
}

const activeStyles = css`
  ${({ theme }) => css`
    border: 2px solid ${theme.colors.primary};
  `}
`;

const StyledSignalAppearanceButton = styled(Card).attrs({
  p: 4,
  role: 'button',
})`
  cursor: pointer;
  display: inline-block;
  flex: 1 0 0px;
  -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
  -moz-box-sizing: border-box; /* Firefox, other Gecko */
  box-sizing: border-box;
  border: 2px solid transparent;
  ${({ active }) => active && activeStyles};
`;

export const getAppearanceIcon = (
  type: SignalAppearanceButtonProps['type'],
  image: string,
  subscriber?: boolean,
): JSX.Element => {
  let icon;

  if (type === SignalAppearanceTypes.Anonymous) {
    icon = signalAppearanceAnonymous();
  } else if (type === SignalAppearanceTypes.Profile) {
    // NOTE: Something off about the styling of the member avatar and the alignment with the ReactSVG. I've had to reduce
    // the size of the member avatar so it aligns with the other buttons
    return (
      <MemberAvatar
        memberAvatarId={image}
        width={50}
        height={50}
        subscribed={subscriber}
      />
    );
  } else if (type === SignalAppearanceTypes.Invisible) {
    icon = signalAppearanceInvisible();
  }

  return (
    <ReactSvg
      toDisplay={icon}
      options={{
        fillColor: '#BABABA',
      }}
      width={50}
      height={50}
    />
  );
};

const StyledCaptionLockup = styled(CaptionedIconLockup)`
  min-height: 80px;
`;

const SignalAppearanceButton: FC<SignalAppearanceButtonProps> = ({
  onClick,
  active,
  loading,
  type,
}) => {
  const { image } = useSelector(selectCurrentMember);

  const title = type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <StyledSignalAppearanceButton
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      active={active}
      onClick={onClick}
    >
      <Shimmer on={loading}>
        <StyledCaptionLockup
          caption={title}
          icon={getAppearanceIcon(type, image)}
        />
      </Shimmer>
    </StyledSignalAppearanceButton>
  );
};

SignalAppearanceButton.defaultProps = {
  active: false,
  loading: false,
};

export default SignalAppearanceButton;
